import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useData } from '../../context/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

// Added FiAlertCircle to the destructured imports
const { FiSave, FiMail, FiServer, FiShield, FiSend, FiAlertCircle } = FiIcons;

const SmtpSettings = () => {
  const { settings, updateSettings } = useData();
  const [formData, setFormData] = useState(settings.smtp);
  const [testEmail, setTestEmail] = useState('');
  const [testStatus, setTestStatus] = useState(null); // 'sending', 'success', 'error'
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    setFormData(settings.smtp);
  }, [settings.smtp]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [name]: checked
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateSettings('smtp', formData);
    setSuccessMsg('SMTP settings saved successfully!');
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleTestEmail = () => {
    if (!testEmail) {
      alert("Please enter a recipient email for the test.");
      return;
    }
    setTestStatus('sending');
    // Simulate SMTP connection check
    setTimeout(() => {
      // Simple validation for simulation
      if (formData.host && formData.username && formData.password) {
        setTestStatus('success');
      } else {
        setTestStatus('error');
      }
      setTimeout(() => setTestStatus(null), 3000);
    }, 1500);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center justify-between mb-6 border-b border-gray-100 pb-4">
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiMail} className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Email & SMTP Configuration</h2>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700">Enable Email Notifications</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="enabled"
              checked={formData.enabled}
              onChange={handleChange}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className={`space-y-6 transition-opacity ${!formData.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Host
              </label>
              <div className="relative">
                <SafeIcon icon={FiServer} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  name="host"
                  value={formData.host}
                  onChange={handleChange}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="smtp.example.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Port
              </label>
              <input
                type="text"
                name="port"
                value={formData.port}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="587"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username / Email
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="email@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Encryption
              </label>
              <div className="relative">
                <SafeIcon icon={FiShield} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  name="encryption"
                  value={formData.encryption}
                  onChange={handleChange}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="none">None</option>
                  <option value="ssl">SSL</option>
                  <option value="tls">TLS</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sender Name
              </label>
              <input
                type="text"
                name="fromName"
                value={formData.fromName}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="CRM System"
              />
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Notification Triggers</h3>
            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="taskAssigned"
                  checked={formData.notifications?.taskAssigned}
                  onChange={handleNotificationChange}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">When a task is assigned to a user</span>
              </label>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="taskCompleted"
                  checked={formData.notifications?.taskCompleted}
                  onChange={handleNotificationChange}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">When a task is marked as completed</span>
              </label>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="deadlineWarning"
                  checked={formData.notifications?.deadlineWarning}
                  onChange={handleNotificationChange}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Warning 24 hours before deadline</span>
              </label>
            </div>
          </div>
        </div>

        {successMsg && (
          <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm">
            {successMsg}
          </div>
        )}

        <div className="flex justify-between items-center pt-4 border-t border-gray-100">
          <div className="flex items-center space-x-3 flex-1 mr-4">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Test recipient email"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-full max-w-xs"
              disabled={!formData.enabled}
            />
            <button
              type="button"
              onClick={handleTestEmail}
              disabled={!formData.enabled || testStatus === 'sending' || !testEmail}
              className="flex items-center space-x-2 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {testStatus === 'sending' ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                  <span>Sending...</span>
                </>
              ) : testStatus === 'success' ? (
                <>
                  <SafeIcon icon={FiSend} className="w-4 h-4 text-green-500" />
                  <span className="text-green-600">Sent!</span>
                </>
              ) : testStatus === 'error' ? (
                <>
                  <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-500" />
                  <span className="text-red-600">Failed</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSend} className="w-4 h-4" />
                  <span>Send Test</span>
                </>
              )}
            </button>
          </div>

          <button
            type="submit"
            className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            <span>Save Configuration</span>
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default SmtpSettings;